import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001';

const TourDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tourData, setTourData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    const fetchTour = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_URL}/api/tours/${id}`);
        setTourData(response.data);
      } catch (err) {
        console.error('Error fetching tour details:', err);
        setError('Tour not found');
        setTourData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTour();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (tourData?.title) {
      document.title = `${tourData.title} | VishwaVoyagerz`;
    }
    return () => {
      document.title = 'VishwaVoyagerz - Tours and Travels';
    };
  }, [tourData]);

  const formatPrice = (price) => {
    if (price === null || price === undefined || isNaN(Number(price))) return 'On Request';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(Number(price));
  };

  const getTourImage = (tour) => {
    if (tour?.images && tour.images.length > 0) {
      const img = tour.images[0];
      if (img && typeof img === 'string' && img.startsWith('/')) {
        return `${API_URL}${img}`;
      }
      return img;
    }
    return 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop';
  };

  const titleCase = (s) => {
    if (!s || typeof s !== 'string') return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = tourData?.title || 'Tour Package';
    const text = `Check out this tour: ${title}`;

    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center mt-20">
          <div className="text-center">
            <i className="fas fa-spinner fa-spin text-4xl text-brand-teal mb-4"></i>
            <p className="text-gray-600">Loading tour details...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !tourData) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center mt-20 px-4">
          <div className="text-center max-w-md">
            <i className="fas fa-map-marked-alt text-5xl text-gray-300 mb-4"></i>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Tour not found</h1>
            <p className="text-gray-600 mb-6">This tour may have been removed or the link is incorrect.</p>
            <button
              onClick={() => navigate('/tours')}
              className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transition"
            >
              Browse all tours
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const destination = tourData.destination || '';
  const country = titleCase(tourData.country);
  const location = [destination, country].filter(Boolean).join(', ') || 'Location not specified';
  const highlights = Array.isArray(tourData.highlights) ? tourData.highlights.filter(Boolean) : [];
  const itinerary = Array.isArray(tourData.itinerary) ? tourData.itinerary : [];
  const inclusions = Array.isArray(tourData.inclusions) ? tourData.inclusions.filter(Boolean) : [];
  const exclusions = Array.isArray(tourData.exclusions) ? tourData.exclusions.filter(Boolean) : [];
  const duration = tourData.duration || null;
  const price = formatPrice(tourData.price);
  const image = getTourImage(tourData);
  const gallery = Array.isArray(tourData.images) ? tourData.images.slice(1, 4) : [];

  return (
    <>
      <Navigation />

      <div className="mt-20">
        {/* Hero image */}
        <section className="relative h-[50vh] min-h-[320px] max-h-[520px] overflow-hidden">
          <img src={image} alt={tourData.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
            <div className="max-w-7xl mx-auto">
              <Link to="/tours" className="inline-flex items-center text-white/90 hover:text-white mb-4 text-sm font-medium">
                <i className="fas fa-arrow-left mr-2"></i>Back to Tours
              </Link>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight">
                {tourData.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-white/90">
                <span className="inline-flex items-center">
                  <i className="fas fa-map-marker-alt mr-2"></i>{location}
                </span>
                {duration && (duration.days || duration.nights) && (
                  <span className="inline-flex items-center bg-white/20 px-3 py-1 rounded-full text-sm">
                    <i className="fas fa-clock mr-2"></i>
                    {duration.days || 0} Days / {duration.nights || 0} Nights
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-10">
              {gallery.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {gallery.map((img, index) => {
                    const src = typeof img === 'string' && img.startsWith('/') ? `${API_URL}${img}` : img;
                    return (
                      <div key={index} className="h-28 sm:h-36 rounded-xl overflow-hidden">
                        <img src={src} alt={`${tourData.title} ${index + 2}`} className="w-full h-full object-cover" />
                      </div>
                    );
                  })}
                </div>
              )}

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <i className="fas fa-info-circle text-brand-teal mr-3"></i>Tour Overview
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed whitespace-pre-line">
                  {tourData.description || 'No description available.'}
                </p>
              </section>

              {highlights.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <i className="fas fa-star text-brand-teal mr-3"></i>Tour Highlights
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {highlights.map((highlight, index) => (
                      <div key={index} className="flex items-start">
                        <i className="fas fa-check-circle text-brand-teal mr-3 mt-1"></i>
                        <span className="text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {itinerary.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <i className="fas fa-calendar-alt text-brand-teal mr-3"></i>Day-by-Day Itinerary
                  </h2>
                  <div className="space-y-4">
                    {itinerary.map((item, index) => (
                      <div key={index} className="border-l-4 border-brand-teal pl-4 py-2">
                        <h3 className="font-bold text-gray-800">Day {item.day}: {item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {(inclusions.length > 0 || exclusions.length > 0) && (
                <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {inclusions.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                        <i className="fas fa-check-circle text-green-500 mr-3"></i>Inclusions
                      </h2>
                      <div className="space-y-3">
                        {inclusions.map((item, index) => (
                          <div key={index} className="flex items-start">
                            <i className="fas fa-check text-green-500 mr-3 mt-1"></i>
                            <span className="text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {exclusions.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                        <i className="fas fa-times-circle text-red-500 mr-3"></i>Exclusions
                      </h2>
                      <div className="space-y-3">
                        {exclusions.map((item, index) => (
                          <div key={index} className="flex items-start">
                            <i className="fas fa-times text-red-500 mr-3 mt-1"></i>
                            <span className="text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}
            </div>

            {/* Sticky booking sidebar */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-28 bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-6 border-2 border-brand-teal shadow-lg">
                <p className="text-gray-600 text-sm mb-1">Starting from</p>
                <div className="text-4xl font-bold bg-gradient-to-r from-brand-teal to-brand-cyan bg-clip-text text-transparent mb-1">
                  {price}
                </div>
                <p className="text-gray-500 text-sm mb-6">per person (based on double occupancy)</p>

                {duration && (duration.days || duration.nights) && (
                  <div className="flex items-center text-gray-700 mb-4 text-sm">
                    <i className="fas fa-clock text-brand-teal mr-2"></i>
                    {duration.days || 0} Days / {duration.nights || 0} Nights
                  </div>
                )}

                <div className="flex items-center text-gray-700 mb-6 text-sm">
                  <i className="fas fa-map-marker-alt text-brand-teal mr-2"></i>
                  {location}
                </div>

                <button
                  onClick={() => setShowBookingModal(true)}
                  className="w-full bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-6 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition transform hover:scale-[1.02] mb-3"
                >
                  <i className="fas fa-calendar-check mr-2"></i>Book This Tour
                </button>

                <button
                  onClick={handleShare}
                  className="w-full bg-white border-2 border-brand-teal text-brand-teal px-6 py-3 rounded-full font-semibold hover:bg-brand-teal hover:text-white transition"
                >
                  <i className={`fas ${shareCopied ? 'fa-check' : 'fa-share-alt'} mr-2`}></i>
                  {shareCopied ? 'Link copied!' : 'Share this tour'}
                </button>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <Footer />

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        tourName={tourData.title}
        tourPrice={price}
        tourId={tourData.id || tourData._id}
      />
    </>
  );
};

export default TourDetail;
